import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react'
import {
  Grid,
  Tooltip,
  IconButton,
  
  Typography,
  Fade,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { withStyles } from '@mui/styles'
import { OrderInfo } from '../../../interfaces/Order/Order'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { Loading } from '../../../components/UI/Loading/Loading'
import { dynamicSort } from '../../../utils/helpers'
import { getOrderDetails } from '../config/api'


// --- Definición de Tipos Específicos ---
interface ViewOrderDetailProps {
  orderInfo: string 
  index: number
  expanded: boolean
  setExpanded: (expanded: boolean) => void
}

// --- Componentes Estilizados ---
const Accordion = withStyles({
})(MuiAccordion)
const AccordionSummary = withStyles({
})(MuiAccordionSummary)
const AccordionDetails = withStyles(() => ({
}))(MuiAccordionDetails)

// --- Constantes ---
const PAGE_SIZE = 5

export const ViewOrderDetail: React.FC<ViewOrderDetailProps> = ({
  orderInfo,
  index,
  expanded,
  setExpanded
}) => {
  // --- Estado del Componente ---
  const [orderDetails, setOrderDetails] = useState<OrderInfo | null>(null)
  const [visibleItemCount, setVisibleItemCount] = useState<number>(PAGE_SIZE)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)

  // --- Estado Derivado (useMemo) ---
  // Procesamos 'item' una sola vez
  const { orderId, orderNumber } = useMemo(() => {
    const parts = orderInfo.split('|')
    return {
      orderId: parseInt(parts[0], 10), // Convertimos a número
      orderNumber: parts[1],
    }
  }, [orderInfo])

  const panelId = `panel${index}`


  // --- Funciones de Lógica ---
  const clearDetails = useCallback(() => {
    setLoading(false)
    setOrderDetails(null)
    setTotalItems(0)
    setVisibleItemCount(PAGE_SIZE)
  }, [])

  const loadOrderDetails = async (id: number) => {
    setLoading(true)
    setOrderDetails(null) // Limpia datos anteriores
    try {
      const response = await getOrderDetails(id)
      
      if (response.data) {
        setOrderDetails(response.data)
        setTotalItems(response.data.orderDetails?.length || 0)
      } else {       
        clearDetails()
      }
    } catch (error) {
      console.error('Error al cargar detalles de la orden:', error)
      clearDetails() // Limpia en caso de error
    } finally {
      setLoading(false)
    }
  }

useEffect(() => {
    if (expanded && !orderDetails && !loading) {
      loadOrderDetails(orderId)
    }
  }, [expanded, orderDetails, loading, loadOrderDetails, orderId])

  // --- Manejadores de Eventos ---
  const handleChange = (
    _event: React.ChangeEvent<{}>,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded)
    if (isExpanded) {
      loadOrderDetails(orderId)
    } else {
      clearDetails()
    }
  }

  const handleShowMore = () => {
    setVisibleItemCount((currentCount) => {
      const newCount = currentCount + PAGE_SIZE
      return newCount > totalItems ? totalItems : newCount
    })
  }

  const handleShowLess = () => {
    setVisibleItemCount(PAGE_SIZE) 
  }

  // --- Renderizado Condicional de Detalles ---
  const renderDetails = () => {
    if (loading) {
      return <Loading />
    }

    if (!orderDetails || !orderDetails.orderDetails || totalItems === 0) {
      return <Typography>Esta orden no tiene detalles.</Typography>
    }

    const hasMore = totalItems > visibleItemCount
    const showLess = visibleItemCount > PAGE_SIZE && totalItems > PAGE_SIZE

    return (
      <Fade in={true}>
        <Grid container direction="row" justifyContent="center" alignItems="center">          
          {/* Encabezados */}
          <Grid item xs={12} sm={12} md={4}>
            {'SKU/EAN'}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            {'Descripción'}
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            {'Cantidad'}
          </Grid>

          {/* Lista de Items */}
          {orderDetails.orderDetails
            .slice(0, visibleItemCount)
            .sort(dynamicSort('name'))
            .map((detail) => (
              <Fragment key={detail.sku.sku}>
                <Grid item xs={12} sm={12} md={4}>
                  {detail.sku.ean13 === detail.sku.sku
                    ? detail.sku.sku
                    : `${detail.sku.ean13}/${detail.sku.sku}`}
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  {detail.sku.name}
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  {detail.quantity}
                </Grid>
              </Fragment>
            ))}

          {/* Botones de Paginación */}
          {hasMore && (
            <Tooltip title="Más..." aria-label="masDetalle">
              <IconButton onClick={handleShowMore}>
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>
          )}
          {showLess && (
            <Tooltip title="Ocultar..." aria-label="ocultarDetalle">
              <IconButton onClick={handleShowLess}>
                <ExpandLessIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      </Fade>
    )
  }


  return (   
    <Grid
      container
      direction="row"
      spacing={2}
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12}>
        <div>
          <Accordion
            expanded={expanded}
            onChange={handleChange}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
            >
              <div>
                <Typography>
                  {`ID Interno: ${orderId}`}{' '}
                </Typography>
                <Typography>
                  {`ID de Pedido: ${orderNumber}`}{' '}
                </Typography>
              </div>
            </AccordionSummary>

            <AccordionDetails>{renderDetails()}</AccordionDetails>
          </Accordion>
        </div>
      </Grid>
    </Grid>
  )
}
